<template>
  <div class="max-w-full h-full m-auto py-4 px-4 flex flex-col space-y-4">
    <OrderCustomerEditVBISlideover
      v-if="slideoverEditAddressVisible"
      title="Edit Order Customer Info"
      :visible="slideoverEditAddressVisible"
      :bad-address="true"
      v-on:toggle-slideover="toggleAddressSlideover"
      v-on:complete="updateRow('complete')"
      v-bind:order="order"
    />
    <OrderRelease
      title="Release Order"
      v-if="sliderOverRelease"
      :visible="sliderOverRelease"
      v-on:toggle-slideover="toggleRelease"
      v-on:update-row="updateRow('status', 'Unapproved')"
    />
    <OrderNotes
      title="Order Notes"
      v-if="slideoverNotesVisible"
      :visible="slideoverNotesVisible"
      :order-number="this.orderNumber"
      v-on:toggle-slideover="toggleNotesSlideover"
    />
    <div class="flex flex-row justify-between space-x-4 py-2">
      <h2 class="text-2xl">Bad Addresses Report</h2>
      <div class="flex space-x-4 items-center">
        <Button
          v-on:click-handler="exportCSV()"
          class="btn btn-primary btn-small flex space-x-2 items-center"
          :disabled="isLoading || !gridHasData"
        >
          <span>Download CSV</span>
          <icon set="outline" name="document-download" class="h-5 w-5" />
        </Button>
        <Button
          v-on:click-handler="loadReport()"
          class="btn btn-primary btn-small flex space-x-2 items-center"
          :disabled="isLoading"
        >
          <span>Refresh Report</span>
          <LoadingSpinner v-if="isLoading" class="h-5 w-5" />
        </Button>
        <Button
          v-if="orderSearching"
          disabled="true"
          class="btn btn-primary btn-small flex space-x-2 items-center"
        >
          <span>Loading Order</span>
          <LoadingSpinner class="h-5 w-5" />
        </Button>
        <ActionsMenu v-else :disabled="disableAction" medium>
          <ActionsItem @click="showEditShippingSlideover">
            Edit Shipping Address
          </ActionsItem>
          <ActionsItem
            v-if="orderCanBeReleased && !restrictedOrder"
            @click="openReleaseSlideover"
          >
            Release Order
          </ActionsItem>
          <ActionsItem
            v-if="orderCanBeHold && !restrictedOrder"
            @click="openHoldModal"
          >
            Hold Order
          </ActionsItem>
          <ActionsItem @click="updateRow('complete')"> Complete </ActionsItem>
          <ActionsItem @click="showNotesSlideover"> View Notes </ActionsItem>
        </ActionsMenu>
      </div>
    </div>

    <ag-grid-vue
      style="height: 100%; width: 100%"
      class="ag-theme-balham"
      :columnDefs="columnDefs"
      :rowData="rowData"
      :paginationAutoPageSize="false"
      :paginationPageSize="paginationPageSize"
      :pagination="true"
      id="tasks-report"
      :gridOptions="gridOptions"
      @grid-ready="onGridReady"
      :context="context"
      :frameworkComponents="frameworkComponents"
      :defaultColDef="defaultColDef"
      :enableCellTextSelection="true"
      :ensureDomOrder="true"
      :singleClickEdit="true"
      :rowSelection="rowSelection"
    >
    </ag-grid-vue>
  </div>
</template>
<script>
import { AgGridVue } from "ag-grid-vue";
import { ApiFactory } from "@/api/ApiFactory.js";
import dateFormatter from "@/mixins/dateFormatter";
import Button from "@/components/elements/Button.vue";
import { date } from "@/components/agGrid/cellRenderers";
import OrderLink from "./components/OrderLink";
import OrderCustomerEditVBISlideover from "@/views/Customers/components/slideovers/VBI/OrderCustomerEditVBI.vue"
import auth from "@/api/auth";
import OrderRelease from "@/views/Customers/components/slideovers/OrderRelease/OrderRelease";
import OrderNotes from "@/views/Reports/components/slideovers/OrderNotes";
import OrderHold from "@/views/Customers/components/modals/OrderHold";
import { ModalBus } from "@/eventBus";
const Reports = ApiFactory.get("reports");

export default {
  name: "BadAddressesReport",
  components: {
    OrderNotes,
    OrderRelease,
    AgGridVue,
    Button,
    OrderCustomerEditVBISlideover,
    OrderLink, // eslint-disable-line
  },
  props: {},
  data() {
    return {
      orderNumber: null,
      columnDefs: null,
      rowData: null,
      paginationPageSize: 100,
      frameworkComponents: null,
      gridApi: null,
      columnApi: null,
      gridOptions: null,
      context: null,
      defaultColDef: null,
      isLoading: false,
      error: null,
      selectedRows: {},
      rowSelection: "single",
      slideoverEditAddressVisible: false,
      sliderOverRelease: false,
      slideoverNotesVisible: false,
    };
  },
  computed: {
    gridHasData() {
      return this.rowData && this.rowData.length;
    },
    hasRowsSelected() {
      return this.selectedRows.length;
    },
    order() {
      return this.$store.getters["orders/get"]("order");
    },
    disableAction() {
      return this.isLoading || !this.gridHasData || !this.hasRowsSelected;
    },
    orderLoaded() {
      return this.$store.getters["orders/get"]("orderLoaded");
    },
    orderSearching() {
      return this.$store.getters["orders/get"]("orderSearching");
    },
    orderCanBeReleased() {
      if (
        this.hasRowsSelected &&
        this.selectedRows[0].Designation === "On Hold"
      ) {
        if (
          this.selectedRows[0].Status == "Hold" &&
          this.selectedRows[0].ExpectedFulfillmentDate == null
        ) {
          return true;
        } else {
          return auth.isUserAuthorized(["supervisor", "third-party"]);
        }
      }
      return false;
    },
    orderCanBeHold() {
      return (
        this.hasRowsSelected && this.selectedRows[0].Designation !== "On Hold"
      );
    },
    restrictedOrder() {
      return (
        this.hasRowsSelected &&
        ["Restricted"].includes(this.selectedRows[0].Status)
      );
    },
    orderIsFreight() {
      if (
        this.hasRowsSelected &&
        Number(this.selectedRows[0].freight_restricted) &&
        !auth.isUserAuthorized(["admin"])
      ) {
        return false;
      }
      return true;
    },
  },
  beforeMount() {
    this.gridOptions = {
      getRowNodeId: (data) => data.SourceOrderNumber,
      onCellValueChanged: (params) => this.updateOrder(params),
      localeText: { noRowsToShow: "No Records Found" },
      onSelectionChanged: this.itemSelectionChanged,
    };

    this.context = { componentParent: this };

    this.defaultColDef = {
      sortable: true,
      filter: true,
      suppressMovable: true,
      resizable: true,
      flex: 1,
    };

    this.columnDefs = [
      {
        headerName: "Address Status",
        field: "BadAddressStatus",
        editable: true,
      },
      {
        headerName: "Reason",
        field: "ReviewReason",
        editable: true,
      },
      {
        headerName: "Order Date",
        field: "OrderDate",
        width: 110,
        cellRenderer: "dateCell",
        valueGetter: (params) => new Date(params.data.OrderDate),
        filter: "agDateColumnFilter",
        filterParams: {
          buttons: ["apply", "reset"],
          suppressAndOrCondition: true,
        },
      },
      {
        headerName: "Order Number",
        field: "SourceOrderNumber",
        cellRendererFramework: "OrderLink",
        cellRendererParams: {
          target: "_blank",
        },
        width: 130,
      },
      {
        headerName: "Status",
        field: "Status",
        width: 100,
      },
      {
        headerName: "Ship Address",
        field: "ShipAddress",
      },
      {
        headerName: "Ship Address 2",
        field: "ShipAddress2",
      },
      {
        headerName: "Ship City",
        field: "ShipCity",
      },
      {
        headerName: "Ship State",
        field: "ShipState",
      },
      {
        headerName: "Ship Zip",
        field: "ShipZip",
        width: 100,
      },
      {
        headerName: "Ship Ref Name",
        field: "RefName",
      },
    ];

    this.frameworkComponents = {
      dateCell: date,
    };

    this.loadReport();
  },
  mounted() {
    this.gridApi = this.gridOptions.api;
    this.gridColumnApi = this.gridOptions.columnApi;
  },
  methods: {
    toggleAddressSlideover() {
      this.slideoverEditAddressVisible = !this.slideoverEditAddressVisible;
    },
    toggleNotesSlideover() {
      this.slideoverNotesVisible = !this.slideoverNotesVisible;
    },
    toggleRelease() {
      this.sliderOverRelease = !this.sliderOverRelease;
    },
    async openHoldModal() {
      await this.fetchOrder();
      ModalBus.$emit("open", {
        component: OrderHold,
        closeOnOutsideClick: false,
      });
    },
    updateRow(row, data) {
      if (this.hasRowsSelected) {
        let rowNode = this.gridApi.getRowNode(
          this.selectedRows[0].SourceOrderNumber
        );
        row = row.toLowerCase();
        if (row === "status") {
          this.selectedRows[0].Designation =
            data === "Unapproved" ? null : "On Hold";
          this.selectedRows[0].Status = data;
          rowNode.setDataValue("Status", data);
        }
        if (row === "complete") {
          rowNode.setDataValue("BadAddressStatus", "Complete");
          this.gridApi.applyTransaction({ remove: [rowNode.data] });
        }
      }
    },
    async fetchOrder() {
      const order =
        this.selectedRows[0] !== undefined ? this.selectedRows[0] : false;
      await this.$store.dispatch("orders/fetchOrder", order.SourceOrderNumber);
    },
    async showEditShippingSlideover() {
      await this.fetchOrder();
      if (this.orderLoaded) {
        this.toggleAddressSlideover();
      }
    },
    showNotesSlideover() {
      this.orderNumber = Number(this.selectedRows[0].SourceOrderNumber);
      this.toggleNotesSlideover();
    },
    async openReleaseSlideover() {
      await this.fetchOrder();
      if (this.orderLoaded) {
        this.toggleRelease();
      }
    },
    onFirstDataRendered(params) {
      params.api.sizeColumnsToFit();
    },
    itemSelectionChanged() {
      this.selectedRows = this.gridOptions.api.getSelectedRows();
    },
    onGridReady() {},
    exportCSV() {
      if (!this.gridHasData) return;
      this.gridApi.exportDataAsCsv({
        fileName: this.$options.name + "-" + dateFormatter.format(new Date()),
      });
    },
    loadReport() {
      this.isLoading = true;
      this.gridOptions?.api?.showLoadingOverlay();

      Reports.getBadAddresses()
        .then((response) => {
          this.rowData = this.removeCompleteOrders(response.data.body);
        })
        .finally(() => (this.isLoading = false));
    },
    removeCompleteOrders(data) {
      return data
        .map((order) => {
          if (
            order.BadAddressStatus === null ||
            order.BadAddressStatus.toLowerCase() !== "complete"
          ) {
            return order;
          }
        })
        .filter(Boolean);
    },
    updateHoldStatus() {
      this.updateRow("status", "Hold");
    },
    updateOrder(params) {
      if (["BadAddressStatus", "ReviewReason"].includes(params.colDef.field)) {
        let data = {
          OrderNumber: params.data.SourceOrderNumber,
        };

        let field =
          params.colDef.field == "BadAddressStatus"
            ? "LocalSortText5"
            : params.colDef.field;

        data[field] = params.value;
        Reports.updateBadAddresses(data).catch((e) => {
          this.error = e;
        });
      }
    },
  },
  filters: {},
  created() {
    ModalBus.$on("hold:update:row", this.updateHoldStatus);
  },
};
</script>
<style lang="scss">
@import "../../../node_modules/ag-grid-community/dist/styles/ag-grid.css";
@import "../../../node_modules/ag-grid-community/dist/styles/ag-theme-balham.css";
</style>
